import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Gift, ChevronRight, Check, Sparkles, Heart } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GiftCardPage = () => {
  const { getContent } = useSiteContent();
  const [step, setStep] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  
  const [formData, setFormData] = useState({
    senderName: '',
    senderEmail: '',
    senderPhone: '',
    recipientName: '',
    recipientEmail: '',
    personalMessage: ''
  });

  const presetAmounts = [
    { value: 2000, label: '₹2,000', popular: false },
    { value: 5000, label: '₹5,000', popular: true },
    { value: 10000, label: '₹10,000', popular: false },
    { value: 15000, label: '₹15,000', popular: false },
    { value: 20000, label: '₹20,000', popular: false },
  ];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCustomAmount(value);
    setSelectedAmount(null);
  };

  const getFinalAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount) return parseInt(customAmount);
    return 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    const amount = getFinalAmount();
    if (amount < 1000) {
      toast.error('Minimum gift card amount is ₹1,000');
      return false;
    }
    if (amount > 50000) {
      toast.error('Maximum gift card amount is ₹50,000');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.senderName.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.senderEmail.trim() || !formData.senderEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (!formData.senderPhone.trim() || formData.senderPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    if (!formData.recipientName.trim()) {
      toast.error('Please enter recipient name');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/gift-cards`, {
        amount: getFinalAmount(),
        sender_name: formData.senderName,
        sender_email: formData.senderEmail,
        sender_phone: formData.senderPhone.startsWith('91') ? formData.senderPhone : `91${formData.senderPhone}`,
        recipient_name: formData.recipientName,
        recipient_email: formData.recipientEmail || null,
        personal_message: formData.personalMessage || null
      });
      
      setOrderDetails(response.data);
      setStep(3);
      toast.success('Gift card order created!');
      
    } catch (error) {
      console.error('Error creating gift card:', error);
      toast.error('Failed to create gift card order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppRedirect = () => {
    if (orderDetails?.whatsapp_url) {
      window.open(orderDetails.whatsapp_url, '_blank');
    }
  };

  return (
    <div data-testid="gift-card-page" className="tattoo-bg-functional min-h-screen">
      <Header />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-20">
        
        {/* Hero Section */}
        <motion.section 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-black rounded-full mb-6">
            <Gift size={36} className="text-white" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black mb-4 tracking-tight">
            {getContent('gift_card_title', 'GIVE THE GIFT OF ART')}
          </h1>
          <p className="font-body text-lg text-gray-600 max-w-xl mx-auto">
            {getContent('gift_card_subtitle', 'Give the gift of meaningful art. Perfect for birthdays, celebrations, or just because.')}
          </p>
        </motion.section>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                step >= s ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <Check size={18} /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 mx-2 transition-all ${step > s ? 'bg-black' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Select Amount */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="paper-texture p-8 md:p-12"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2 text-center">
                {getContent('gift_card_step1_title', 'Choose Amount')}
              </h2>
              <p className="font-body text-gray-600 text-center mb-8">
                {getContent('gift_card_step1_subtitle', 'Choose a preset amount or enter a custom value')}
              </p>

              {/* Preset Amounts */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-8">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount.value}
                    onClick={() => handleAmountSelect(amount.value)}
                    data-testid={`amount-${amount.value}`}
                    className={`relative p-4 border-2 transition-all ${
                      selectedAmount === amount.value
                        ? 'border-black bg-black text-white'
                        : 'border-gray-300 hover:border-black'
                    }`}
                  >
                    {amount.popular && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-400 text-black text-[10px] font-bold px-2 py-0.5 uppercase">
                        Popular
                      </span>
                    )}
                    <span className="font-heading text-xl font-bold">{amount.label}</span>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <label className="font-heading text-sm font-bold uppercase tracking-wider mb-2 block">
                  Or Enter Custom Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-heading text-xl">₹</span>
                  <input
                    type="text"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    placeholder="Enter amount (min ₹1,000)"
                    data-testid="custom-amount-input"
                    className={`w-full pl-10 pr-4 py-4 border-2 font-heading text-xl focus:outline-none transition-all ${
                      customAmount ? 'border-black' : 'border-gray-300 focus:border-black'
                    }`}
                  />
                </div>
                <p className="font-mono text-xs text-gray-500 mt-2">
                  Minimum: ₹1,000 • Maximum: ₹50,000
                </p>
              </div>

              {/* Selected Amount Display */}
              {getFinalAmount() > 0 && (
                <div className="bg-gray-100 p-4 mb-8 text-center">
                  <p className="font-mono text-sm text-gray-600 mb-1">Selected Amount</p>
                  <p className="font-heading text-4xl font-black">₹{getFinalAmount().toLocaleString()}</p>
                </div>
              )}

              <button
                onClick={handleNextStep}
                disabled={getFinalAmount() < 1000}
                data-testid="step1-next-btn"
                className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                Continue <ChevronRight size={18} />
              </button>
            </motion.div>
          )}

          {/* Step 2: Enter Details */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="paper-texture p-8 md:p-12"
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2 text-center">
                {getContent('gift_card_step2_title', 'Gift Details')}
              </h2>
              <p className="font-body text-gray-600 text-center mb-8">
                {getContent('gift_card_step2_subtitle', "Tell us about you and the recipient")}
              </p>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Sender Info */}
                <div>
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Sparkles size={18} /> Your Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Your Name *</label>
                      <input
                        type="text"
                        name="senderName"
                        value={formData.senderName}
                        onChange={handleInputChange}
                        data-testid="sender-name-input"
                        className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Your Email *</label>
                      <input
                        type="email"
                        name="senderEmail"
                        value={formData.senderEmail}
                        onChange={handleInputChange}
                        data-testid="sender-email-input"
                        className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Your Phone *</label>
                      <input
                        type="tel"
                        name="senderPhone"
                        value={formData.senderPhone}
                        onChange={handleInputChange}
                        data-testid="sender-phone-input"
                        className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        placeholder="9876543210"
                      />
                    </div>
                  </div>
                </div>

                {/* Recipient Info */}
                <div>
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Heart size={18} /> Recipient Information
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Recipient Name *</label>
                      <input
                        type="text"
                        name="recipientName"
                        value={formData.recipientName}
                        onChange={handleInputChange}
                        data-testid="recipient-name-input"
                        className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        placeholder="Who is this gift for?"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Recipient Email (Optional)</label>
                      <input
                        type="email"
                        name="recipientEmail"
                        value={formData.recipientEmail}
                        onChange={handleInputChange}
                        data-testid="recipient-email-input"
                        className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
                        placeholder="recipient@email.com"
                      />
                      <p className="font-mono text-xs text-gray-500 mt-1">
                        We'll send the gift card to this email after payment
                      </p>
                    </div>
                    <div>
                      <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Personal Message (Optional)</label>
                      <textarea
                        name="personalMessage"
                        value={formData.personalMessage}
                        onChange={handleInputChange}
                        data-testid="personal-message-input"
                        rows={3}
                        className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
                        placeholder="Add a heartfelt message..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-100 p-4 mt-8 flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-gray-600">Gift Card Amount</p>
                  <p className="font-heading text-2xl font-black">₹{getFinalAmount().toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-xs text-gray-600">Valid for</p>
                  <p className="font-heading text-lg font-bold">6 Months</p>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 border-2 border-black py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-100 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleNextStep}
                  disabled={isSubmitting}
                  data-testid="step2-submit-btn"
                  className="flex-1 bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? 'Creating...' : 'Proceed to Payment'}
                  <ChevronRight size={18} />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Confirmation & WhatsApp */}
          {step === 3 && orderDetails && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="paper-texture p-8 md:p-12 text-center"
            >
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={40} className="text-white" />
              </div>
              
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                {getContent('gift_card_step3_title', 'Order Created Successfully!')}
              </h2>
              <p className="font-body text-gray-600 mb-8 max-w-md mx-auto">
                {getContent('gift_card_step3_subtitle', 'Complete your payment via WhatsApp to receive your gift card')}
              </p>

              {/* Order Summary */}
              <div className="bg-black text-white p-6 mb-8 max-w-sm mx-auto">
                <p className="font-mono text-xs text-gray-400 mb-2">Order Summary</p>
                <p className="font-heading text-4xl font-black mb-4">₹{getFinalAmount().toLocaleString()}</p>
                <div className="text-left space-y-2 text-sm">
                  <p><span className="text-gray-400">For:</span> {formData.recipientName}</p>
                  <p><span className="text-gray-400">From:</span> {formData.senderName}</p>
                  <p><span className="text-gray-400">Order ID:</span> {orderDetails.id?.slice(0, 8)}...</p>
                </div>
              </div>

              {/* Payment Instructions */}
              <div className="bg-amber-50 border-2 border-amber-200 p-6 mb-8 text-left max-w-md mx-auto">
                <h3 className="font-heading text-lg font-bold mb-3">{getContent('gift_card_payment_title', 'Payment Instructions')}</h3>
                <ol className="font-body text-sm space-y-2 list-decimal list-inside">
                  <li>Click the WhatsApp button below</li>
                  <li>Send the pre-filled message</li>
                  <li>Our team will share UPI payment details</li>
                  <li>Complete payment via UPI/GPay/PhonePe</li>
                  <li>Share payment screenshot for confirmation</li>
                  <li>Receive your digital gift card!</li>
                </ol>
              </div>

              <button
                onClick={handleWhatsAppRedirect}
                data-testid="whatsapp-payment-btn"
                className="w-full max-w-md mx-auto bg-green-600 text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-green-700 transition-colors flex items-center justify-center gap-3"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Complete Payment on WhatsApp
              </button>

              <p className="font-mono text-xs text-gray-500 mt-4">
                Gift card will be delivered via WhatsApp & Email after payment confirmation
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Features Section */}
        {step === 1 && (
          <motion.section 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-16 grid md:grid-cols-3 gap-8"
          >
            {[
              {
                title: 'Instant Delivery',
                desc: 'Digital gift card delivered via WhatsApp & Email',
              },
              {
                title: '6 Month Validity',
                desc: 'Plenty of time to plan the perfect tattoo',
              },
              {
                title: 'Any Service',
                desc: 'Redeemable for any tattoo or service at Vision Tattoo',
              },
            ].map((feature, index) => (
              <div key={index} className="text-center">
                <h3 className="font-heading text-lg font-bold mb-2">{feature.title}</h3>
                <p className="font-body text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </motion.section>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default GiftCardPage;
